import { useState } from 'react'
import { api } from '../services/api'

/**
 * Hook para gerenciar imagens de múltiplos tipos (hotels, cars, tours)
 *
 * Elimina duplicação de handleHotelImageChange, handleCarImageChange, handleTourImageChange
 * 
 * Regras de negócio:
 *   - Hotéis: 3 fotos
 *   - Carros: 3 fotos
 *   - Tours: 1 foto
 */
export function useImageManager() {
    const [hotelImages, setHotelImages] = useState({})
    const [carImages, setCarImages] = useState({})
    const [tourImages, setTourImages] = useState({})

    const handleImageChange = (type, itemName, imageKey, value) => {
        const setters = {
            hotel: setHotelImages,
            car: setCarImages,
            tour: setTourImages
        }

        const setter = setters[type]
        if (setter) {
            setter((prev) => ({
                ...prev,
                [itemName]: {
                    ...(prev[itemName] || {}),
                    [imageKey]: value
                }
            }))
        }
    }

    const loadImagesForBlocks = async (blocks) => {
        const results = {
            hotels: {},
            cars: {},
            tours: {}
        }

        // Hotels
        const hotels = blocks.filter((b) => b.type === 'hotel')
        const hotelNames = hotels.map((h) => h.title)

        if (hotelNames.length > 0) {
            const hotelImagesResult = await api.getHotelsImagesBatch(hotelNames)
            hotelNames.forEach((name) => {
                const existing = hotelImagesResult?.hotels?.[name] || {}
                results.hotels[name] = {
                    img1: existing.image_url || '',
                    img2: existing.image_url_2 || '',
                    img3: existing.image_url_3 || '',
                    existing: {
                        img1: existing.found ? (existing.image_url || null) : null,
                        img2: existing.found ? (existing.image_url_2 || null) : null,
                        img3: existing.found ? (existing.image_url_3 || null) : null
                    }
                }
            })
            setHotelImages(results.hotels)
        }

        // Cars
        const cars = blocks.filter((b) => b.type === 'car')
        const carNames = cars.map((c) => c.title)

        if (carNames.length > 0) {
            const carImagesResult = await api.getCarsImagesBatch(carNames)
            carNames.forEach((name) => {
                const existing = carImagesResult?.cars?.[name] || {}
                results.cars[name] = {
                    img1: existing.image_url || '',
                    img2: existing.image_url_2 || '',
                    img3: existing.image_url_3 || '',
                    existing: {
                        img1: existing.found ? (existing.image_url || null) : null,
                        img2: existing.found ? (existing.image_url_2 || null) : null,
                        img3: existing.found ? (existing.image_url_3 || null) : null
                    }
                }
            })
            setCarImages(results.cars)
        }

        // Tours - apenas 1 imagem
        const tours = blocks.filter((b) => b.type === 'tour')
        const tourNames = tours.map((t) => t.title)

        if (tourNames.length > 0) {
            const tourImagesResult = await api.getToursImagesBatch(tourNames)
            tourNames.forEach((name) => {
                const existing = tourImagesResult?.tours?.[name] || {}
                results.tours[name] = {
                    img1: existing.image_url || '',
                    existing: {
                        img1: existing.found ? (existing.image_url || null) : null
                    }
                }
            })
            setTourImages(results.tours)
        }

        return results
    }

    const saveAllImages = async () => {
        const hotelsToSave = Object.entries(hotelImages).map(([name, imgs]) => ({
            name,
            image_url: imgs.img1,
            image_url_2: imgs.img2,
            image_url_3: imgs.img3
        }))

        if (hotelsToSave.length > 0) {
            await api.saveHotelsImagesBatch(hotelsToSave)
        }

        const carsToSave = Object.entries(carImages).map(([name, imgs]) => ({
            name,
            image_url: imgs.img1,
            image_url_2: imgs.img2,
            image_url_3: imgs.img3
        }))

        if (carsToSave.length > 0) {
            await api.saveCarsImagesBatch(carsToSave)
        }

        // Tours - apenas 1 imagem
        const toursToSave = Object.entries(tourImages).map(([name, imgs]) => ({
            name,
            image_url: imgs.img1
        }))

        if (toursToSave.length > 0) {
            await api.saveToursImagesBatch(toursToSave)
        }
    }

    // Valida 3 imagens (para hotéis e carros)
    const validateImagesData3 = (images) => {
        for (const imgs of Object.values(images)) {
            if (!String(imgs.img1 || '').trim() || !String(imgs.img2 || '').trim() || !String(imgs.img3 || '').trim()) {
                return false
            }
        }
        return true
    }

    // Valida 1 imagem (para tours)
    const validateImagesData1 = (images) => {
        for (const imgs of Object.values(images)) {
            if (!String(imgs.img1 || '').trim()) {
                return false
            }
        }
        return true
    }

    const validateAllImages = (hotelsData = null, carsData = null, toursData = null) => {
        const hotels = hotelsData || hotelImages
        const cars = carsData || carImages
        const tours = toursData || tourImages

        // Hotéis e carros precisam de 3 imagens, tours apenas 1
        return validateImagesData3(hotels) && validateImagesData3(cars) && validateImagesData1(tours)
    }

    const needsAnyImages = () => {
        const checkNeeds3 = (images) => {
            return Object.values(images).some((imgs) => {
                const ex = imgs.existing || {}
                return !ex.img1 || !ex.img2 || !ex.img3
            })
        }

        const checkNeeds1 = (images) => {
            return Object.values(images).some((imgs) => {
                const ex = imgs.existing || {}
                return !ex.img1
            })
        }

        return checkNeeds3(hotelImages) || checkNeeds3(carImages) || checkNeeds1(tourImages)
    }

    // Mantém compatibilidade com código antigo
    const validateImagesData = validateImagesData3

    return {
        hotelImages,
        carImages,
        tourImages,
        handleImageChange,
        loadImagesForBlocks,
        saveAllImages,
        validateImagesData,
        validateAllImages,
        needsAnyImages
    }
}
